import styled from "@emotion/styled";
import { size, padding } from "polished";
import React from "react";

import { Link } from "@brexhq/next/src/components/shared/Link";
import { buildUrlFromSlug } from "@brexhq/shared/utils/buildUrlFromSlug";

import { IArticleListNavModule, INavModuleProps } from "../../types";

const List = styled.ul(({ theme }) => ({
  listStyle: "none",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.LRG.sml,
  padding: 0,
  margin: 0,
  [theme.breakpoints.nav.SML]: {
    gap: 0,
    "> li": {
      borderBottom: `1px solid ${theme.colors.border.default}`,
      "&:first-of-type": {
        borderTop: `1px solid ${theme.colors.border.default}`,
      },
    },
  },
}));

const ModuleLink = styled(Link)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: theme.spacing.LRG.sml,
  borderRadius: theme.borders.radius.default,
  ...padding(theme.spacing.LRG.xxsml),
  "&:hover": {
    background: theme.colors.border.default,
  },
  [theme.breakpoints.nav.SML]: {
    ...padding(theme.spacing.SML.xsml, theme.spacing.SML.sml),
    borderRadius: "unset",
    "&:hover": {
      background: "unset",
    },
  },
}));

const Title = styled.div(({ theme }) => ({
  ...theme.typography.BodySmallSB,
  color: theme.colors.content.primary,
  [theme.breakpoints.nav.MED]: {
    fontSize: 12,
  },
}));

const Image = styled.img(({ theme }) => ({
  ...size("100%"),
  width: 60,
  height: 60,
  objectFit: "cover",
  borderRadius: theme.borders.radius.default,
}));

export default function ArticleList({
  articles,
}: INavModuleProps<IArticleListNavModule>) {
  return (
    <List>
      {(articles || []).map(({ _key, page, titleOverride, imageOverride }) => {
        const title = titleOverride || page.meta.title;
        const image =
          imageOverride ||
          (page._type === "contentHubArticle"
            ? page.contentHubArticleContent.articleImage.image
            : page.corePageContent.image.image);
        const to = buildUrlFromSlug({
          pageType: page._type,
          slug: page.meta.slug.current,
          parent:
            page._type === "contentHubArticle"
              ? page.contentHubArticleContent.articleGroup?.parent
              : undefined,
        });
        return (
          <li key={_key}>
            <ModuleLink
              component="ArticleList"
              modifier="unstyled"
              to={to}
              label={title}
              tabIndex={-1}
            >
              <Title>{title}</Title>
              {image?.datUrl ? (
                <Image
                  src={image.datUrl}
                  alt={image.description || undefined}
                />
              ) : null}
            </ModuleLink>
          </li>
        );
      })}
    </List>
  );
}
